import React, { useState, useEffect, useRef } from 'react';

/* Design tokens — contrast-audited on #05070a background */
export const C = {
  lime: '#c8f135',
  limeHover: '#d4f74a',
  bg: '#05070a',
  surface: 'rgba(255,255,255,0.025)',
  surfaceDeep: 'rgba(0,0,0,0.35)',
  border: 'rgba(255,255,255,0.09)',
  borderLime: 'rgba(200,241,53,0.25)',
  text: '#f2f5ea',
  body: '#ced8c2',
  muted: '#a2b093',
};

export const DISP = "'Syne', system-ui, sans-serif";
export const BODY = "'Space Grotesk', system-ui, sans-serif";
export const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

/* Section-level analytics (PostHog loaded in index.html) */
export const track = (event, props = {}) => {
  try {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(event, props);
    }
  } catch (e) { /* analytics must never break the page */ }
};

export const useInView = (ref, threshold = 0.12) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setSeen(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold, seen]);
  return seen;
};

/* Fires a section_viewed analytics event once when the section scrolls into view */
export const useSectionView = (sectionName) => {
  const ref = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    if (!ref.current || fired.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !fired.current) {
        fired.current = true;
        track('landing_section_viewed', { section: sectionName });
        obs.disconnect();
      }
    }, { threshold: 0.25 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [sectionName]);
  return ref;
};

export const FadeUp = ({ children, delay = 0, style = {}, as: As = 'div', ...rest }) => {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <As ref={ref} className={`scl-fade-in ${inView ? 'in' : ''}`} style={{ transitionDelay: `${delay}ms`, ...style }} {...rest}>
      {children}
    </As>
  );
};

export const Kicker = ({ children, center }) => (
  <p style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 16, textAlign: center ? 'center' : 'left' }}>{children}</p>
);

export const SectionH2 = ({ children, center, style = {} }) => (
  <h2 style={{ fontFamily: DISP, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.04, color: C.text, fontSize: 'clamp(30px, 4.6vw, 54px)', textAlign: center ? 'center' : 'left', ...style }}>
    {children}
  </h2>
);

export const SectionSub = ({ children, center, style = {} }) => (
  <p style={{ marginTop: 18, fontFamily: BODY, fontSize: 16.5, lineHeight: 1.75, color: C.body, maxWidth: 620, textAlign: center ? 'center' : 'left', marginLeft: center ? 'auto' : 0, marginRight: center ? 'auto' : 0, ...style }}>
    {children}
  </p>
);

export const SECTION_PAD = 'clamp(64px, 8vw, 120px) clamp(20px, 5vw, 48px)';

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const GlobalStyle = () => (
  <style>{`
    * { box-sizing: border-box; margin: 0; padding: 0; overflow-wrap: break-word; }
    body { overflow-x: hidden; background: ${C.bg}; color: ${C.text}; }
    html { scroll-behavior: smooth; }
    button { font-family: inherit; cursor: pointer; border: none; background: none; color: inherit; }
    input { font-family: inherit; }
    a { color: inherit; text-decoration: none; }
    section[id], div[id="scanner"], div[id="demo"] { scroll-margin-top: 84px; }
    ::selection { background: ${C.lime}; color: ${C.bg}; }
    :focus-visible { outline: 2px solid ${C.lime}; outline-offset: 3px; border-radius: 4px; }

    @keyframes scl-pulse { 0%,100% { opacity:1; transform:scale(1);} 50% { opacity:0.55; transform:scale(0.85);} }
    @keyframes scl-marquee { 0% { transform:translateX(0);} 100% { transform:translateX(-50%);} }

    .scl-fade-in { opacity: 0; transform: translateY(32px); transition: opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1); }
    .scl-fade-in.in { opacity: 1; transform: translateY(0); }
    .scl-pulse-dot { animation: scl-pulse 2s ease-in-out infinite; }
    .scl-marquee { animation: scl-marquee 30s linear infinite; }

    .scl-btn-primary {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      background: ${C.lime}; color: ${C.bg};
      padding: 16px 28px; border-radius: 12px;
      font-family: ${BODY}; font-size: 15px; font-weight: 700; letter-spacing: 0.01em;
      min-height: 48px;
      box-shadow: 0 0 36px rgba(200,241,53,0.25);
      transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), box-shadow 0.25s, background 0.25s;
    }
    .scl-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 0 56px rgba(200,241,53,0.4); background: ${C.limeHover}; }
    .scl-btn-ghost {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      background: transparent; color: ${C.text};
      padding: 15px 26px; border-radius: 12px;
      border: 1px solid ${C.border};
      font-family: ${BODY}; font-size: 15px; font-weight: 600;
      min-height: 48px;
      transition: border-color 0.25s, background 0.25s, color 0.25s;
    }
    .scl-btn-ghost:hover { border-color: ${C.borderLime}; color: ${C.lime}; background: rgba(200,241,53,0.04); }

    .scl-card { transition: border-color 0.3s, background 0.3s, transform 0.3s cubic-bezier(0.16,1,0.3,1); }
    .scl-card:hover { border-color: ${C.borderLime}; background: rgba(200,241,53,0.03); }

    .scl-nav-desktop { display: flex; }
    .scl-nav-burger { display: none; }
    @media (max-width: 820px) {
      .scl-nav-desktop { display: none; }
      .scl-nav-burger { display: inline-flex; }
    }
    @media (max-width: 900px) {
      .scl-hero-grid { grid-template-columns: 1fr !important; gap: 44px !important; }
      .scl-two-col { grid-template-columns: 1fr !important; }
    }
    @media (max-width: 640px) {
      .scl-hero-ctas { flex-direction: column; align-items: stretch !important; }
      .scl-hero-ctas > * { width: 100%; }
    }

    @media (prefers-reduced-motion: reduce) {
      html { scroll-behavior: auto; }
      *, *::before, *::after { animation: none !important; transition: none !important; }
      .scl-fade-in { opacity: 1; transform: none; }
    }
  `}</style>
);
